import { SuccessToast } from '@strata/react-toast-message';
import { useCallback, useEffect, useState } from 'react';

import { handleError } from 'services/ErrorManagement';
import { fetchPrescriberByNpi, reactivatePrescriber } from 'services/PrescriberReference';

export const usePrescriberLoad = (npi, isCreate) => {
    const [loadState, setLoadState] = useState(isCreate ? { kind: 'ready' } : { kind: 'loading' });
    const [prescriber, setPrescriber] = useState();
    const [isReactivating, setIsReactivating] = useState(false);

    const loadPrescriber = useCallback(async () => {
        if (isCreate) {
            return;
        }

        setLoadState({ kind: 'loading' });
        try {
            const result = await fetchPrescriberByNpi(npi);
            if (result.status === 'active') {
                setPrescriber(result.prescriber);
                setLoadState({ kind: 'ready' });
            } else if (result.status === 'deactivated') {
                setPrescriber(result.prescriber ?? undefined);
                setLoadState({
                    kind: 'deactivated',
                    message: result.message,
                });
            } else {
                setPrescriber(undefined);
                setLoadState({ kind: 'not_found' });
            }
        } catch (err) {
            handleError('Error fetching prescriber data', err);
            setLoadState({ kind: 'error' });
        }
    }, [isCreate, npi]);

    useEffect(() => {
        if (isCreate) {
            return undefined;
        }

        let isCancelled = false;

        const runLoad = async () => {
            setLoadState({ kind: 'loading' });
            try {
                const result = await fetchPrescriberByNpi(npi);
                if (isCancelled) {
                    return;
                }

                if (result.status === 'active') {
                    setPrescriber(result.prescriber);
                    setLoadState({ kind: 'ready' });
                } else if (result.status === 'deactivated') {
                    setPrescriber(result.prescriber ?? undefined);
                    setLoadState({
                        kind: 'deactivated',
                        message: result.message,
                    });
                } else {
                    setPrescriber(undefined);
                    setLoadState({ kind: 'not_found' });
                }
            } catch (err) {
                if (isCancelled) {
                    return;
                }
                handleError('Error fetching prescriber data', err);
                setLoadState({ kind: 'error' });
            }
        };

        runLoad();

        return () => {
            isCancelled = true;
        };
    }, [isCreate, npi]);

    const handleReactivate = async () => {
        try {
            setIsReactivating(true);
            const message = await reactivatePrescriber(npi);
            await SuccessToast({ message });
            await loadPrescriber();
        } catch (err) {
            handleError('Error reactivating prescriber.', err);
        } finally {
            setIsReactivating(false);
        }
    };

    return {
        loadState,
        prescriber,
        setPrescriber,
        isReactivating,
        loadPrescriber,
        handleReactivate,
    };
};
